import { useEffect, useLayoutEffect, useState, type RefObject } from 'react';
import { useAccountContext } from '../Providers';
import { AccountSettingsPage } from '../SettingsPages';

export interface AccountSettingsPanelProps {
  /**
   * The entity header's root element: the panel covers everything below it,
   * so the header and the tab row stay visible while settings are open.
   */
  anchorRef: RefObject<HTMLElement | null>;
}

/**
 * Account settings slide-over for account pages. Unlike the overview-tab
 * implementation it works from every account tab: it is fixed to the viewport
 * below the header and locks the body scroll while open, replacing the tab
 * content area without unmounting it.
 */
export function AccountSettingsPanel({ anchorRef }: AccountSettingsPanelProps) {
  const { isSettingsOpen } = useAccountContext();
  const [anchorBottom, setAnchorBottom] = useState(0);

  useLayoutEffect(() => {
    if (!isSettingsOpen) {
      return undefined;
    }
    const measure = () => {
      const anchor = anchorRef.current;
      if (anchor) {
        setAnchorBottom(anchor.getBoundingClientRect().bottom);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isSettingsOpen, anchorRef]);

  useEffect(() => {
    if (!isSettingsOpen) {
      return undefined;
    }
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [isSettingsOpen]);

  if (!isSettingsOpen) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: anchorBottom,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
        zIndex: 10,
      }}
    >
      <AccountSettingsPage variant="fixed" />
    </div>
  );
}
